/**
 * Card Detection Utility
 * Detects when a business card is properly positioned in the camera frame
 * Similar to Genius Scan - detects actual rectangular card boundaries
 */

/**
 * Analyzes video frame to detect if a card-like rectangle is present and in focus
 * @param {HTMLVideoElement} video - The video element
 * @param {HTMLCanvasElement} canvas - Canvas for analysis
 * @returns {Object} Detection result with status and confidence
 */
export const detectCard = (video, canvas) => {
  if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
    return { detected: false, reason: 'video_not_ready' };
  }

  const ctx = canvas.getContext('2d');
  
  // Set canvas to a reasonable size for analysis (smaller is faster)
  const analyzeWidth = 640;
  const analyzeHeight = Math.floor(video.videoHeight * (analyzeWidth / video.videoWidth));
  
  canvas.width = analyzeWidth;
  canvas.height = analyzeHeight;
  
  // Draw current video frame
  ctx.drawImage(video, 0, 0, analyzeWidth, analyzeHeight);
  
  try {
    // Get image data for analysis
    const imageData = ctx.getImageData(0, 0, analyzeWidth, analyzeHeight);
    
    // Check brightness first - not too dark or too bright
    const brightness = calculateBrightness(imageData);
    const goodBrightness = brightness > 60 && brightness < 210;
    
    if (!goodBrightness) {
      return { 
        detected: false, 
        reason: brightness < 60 ? 'too_dark' : 'too_bright', 
        brightness 
      };
    }
    
    // Check image sharpness (blur detection)
    const sharpness = calculateSharpness(imageData);
    const isSharp = sharpness > 25; // Higher threshold - must be quite sharp
    
    if (!isSharp) {
      return { detected: false, reason: 'blurry', sharpness };
    }
    
    // Detect actual rectangular contours (the card boundaries)
    const rectangles = detectRectangularContours(imageData, analyzeWidth, analyzeHeight);
    
    if (rectangles.length === 0) {
      return { detected: false, reason: 'no_card_detected' };
    }
    
    // Find the best candidate rectangle (largest, well-positioned)
    const cardRect = findBestCardCandidate(rectangles, analyzeWidth, analyzeHeight);
    
    if (!cardRect) {
      return { detected: false, reason: 'card_not_aligned' };
    }
    
    // Verify the card is properly positioned within the expected guide area
    const isWellPositioned = verifyCardPosition(cardRect, analyzeWidth, analyzeHeight);
    
    if (!isWellPositioned) {
      return { detected: false, reason: 'card_not_aligned' };
    }
    
    // Check if the card has good contrast (edges are clear)
    const contrast = calculateContrast(imageData, cardRect);
    const hasGoodContrast = contrast > 35;
    
    if (!hasGoodContrast) {
      return { detected: false, reason: 'low_contrast', contrast };
    }
    
    // All conditions met!
    return { 
      detected: true, 
      confidence: Math.min(100, Math.floor((sharpness / 40 + contrast / 60) * 50)),
      sharpness,
      contrast,
      brightness,
      cardRect
    };
    
  } catch (error) {
    console.error('Error in card detection:', error);
    return { detected: false, reason: 'analysis_error' };
  }
};

/**
 * Calculate image sharpness using Laplacian variance
 * Higher values = sharper image
 */
function calculateSharpness(imageData) {
  const { data, width, height } = imageData;
  let variance = 0;
  let mean = 0;
  let count = 0;
  
  // Sample every 4th pixel for performance
  for (let y = 1; y < height - 1; y += 4) {
    for (let x = 1; x < width - 1; x += 4) {
      const idx = (y * width + x) * 4;
      
      // Calculate grayscale value
      const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
      
      // Simple Laplacian approximation (measures local variance)
      const topIdx = ((y - 1) * width + x) * 4;
      const bottomIdx = ((y + 1) * width + x) * 4;
      const leftIdx = (y * width + (x - 1)) * 4;
      const rightIdx = (y * width + (x + 1)) * 4;
      
      const topGray = (data[topIdx] + data[topIdx + 1] + data[topIdx + 2]) / 3;
      const bottomGray = (data[bottomIdx] + data[bottomIdx + 1] + data[bottomIdx + 2]) / 3;
      const leftGray = (data[leftIdx] + data[leftIdx + 1] + data[leftIdx + 2]) / 3;
      const rightGray = (data[rightIdx] + data[rightIdx + 1] + data[rightIdx + 2]) / 3;
      
      const laplacian = Math.abs(4 * gray - topGray - bottomGray - leftGray - rightGray);
      
      mean += laplacian;
      count++;
    }
  }
  
  mean /= count;
  
  // Calculate variance
  for (let y = 1; y < height - 1; y += 4) {
    for (let x = 1; x < width - 1; x += 4) {
      const idx = (y * width + x) * 4;
      const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
      
      const topIdx = ((y - 1) * width + x) * 4;
      const bottomIdx = ((y + 1) * width + x) * 4;
      const leftIdx = (y * width + (x - 1)) * 4;
      const rightIdx = (y * width + (x + 1)) * 4;
      
      const topGray = (data[topIdx] + data[topIdx + 1] + data[topIdx + 2]) / 3;
      const bottomGray = (data[bottomIdx] + data[bottomIdx + 1] + data[bottomIdx + 2]) / 3;
      const leftGray = (data[leftIdx] + data[leftIdx + 1] + data[leftIdx + 2]) / 3;
      const rightGray = (data[rightIdx] + data[rightIdx + 1] + data[rightIdx + 2]) / 3;
      
      const laplacian = Math.abs(4 * gray - topGray - bottomGray - leftGray - rightGray);
      
      variance += Math.pow(laplacian - mean, 2);
    }
  }
  
  return Math.sqrt(variance / count);
}

/**
 * Detect rectangular contours in the image (actual card boundaries)
 * Uses edge detection and contour finding similar to Genius Scan
 */
function detectRectangularContours(imageData, width, height) {
  const { data } = imageData;
  
  // Create edge map using Sobel operator
  const edgeMap = new Uint8Array(width * height);
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = (y * width + x) * 4;
      
      // Get grayscale value
      const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
      
      // Sobel kernels
      const gx = 
        -gray + getGray(data, x + 1, y - 1, width) +
        -2 * getGray(data, x - 1, y, width) + 2 * getGray(data, x + 1, y, width) +
        -getGray(data, x - 1, y + 1, width) + getGray(data, x + 1, y + 1, width);
      
      const gy = 
        -gray - 2 * getGray(data, x, y - 1, width) - getGray(data, x + 1, y - 1, width) +
        getGray(data, x - 1, y + 1, width) + 2 * getGray(data, x, y + 1, width) + getGray(data, x + 1, y + 1, width);
      
      const magnitude = Math.sqrt(gx * gx + gy * gy);
      edgeMap[y * width + x] = magnitude > 50 ? 255 : 0;
    }
  }
  
  // Find connected edge components that could be rectangles
  const rectangles = findRectanglesInEdgeMap(edgeMap, width, height);
  
  return rectangles;
}

function getGray(data, x, y, width) {
  const idx = (y * width + x) * 4;
  return (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
}

/**
 * Find rectangles in edge map by detecting straight lines and corners
 */
function findRectanglesInEdgeMap(edgeMap, width, height) {
  const rectangles = [];
  
  // Scan for horizontal and vertical edge segments
  const horizontalLines = [];
  const verticalLines = [];
  
  // Find horizontal lines
  for (let y = Math.floor(height * 0.15); y < height * 0.85; y += 3) {
    let lineStart = -1;
    let edgeCount = 0;
    
    for (let x = 0; x < width; x++) {
      if (edgeMap[y * width + x] > 0) {
        if (lineStart === -1) lineStart = x;
        edgeCount++;
      } else if (lineStart !== -1 && edgeCount > width * 0.15) {
        horizontalLines.push({ y, x1: lineStart, x2: x - 1, length: edgeCount });
        lineStart = -1;
        edgeCount = 0;
      } else {
        lineStart = -1;
        edgeCount = 0;
      }
    }
  }
  
  // Find vertical lines
  for (let x = Math.floor(width * 0.1); x < width * 0.9; x += 3) {
    let lineStart = -1;
    let edgeCount = 0;
    
    for (let y = 0; y < height; y++) {
      if (edgeMap[y * width + x] > 0) {
        if (lineStart === -1) lineStart = y;
        edgeCount++;
      } else if (lineStart !== -1 && edgeCount > height * 0.15) {
        verticalLines.push({ x, y1: lineStart, y2: y - 1, length: edgeCount });
        lineStart = -1;
        edgeCount = 0;
      } else {
        lineStart = -1;
        edgeCount = 0;
      }
    }
  }
  
  // Try to form rectangles from lines
  if (horizontalLines.length >= 2 && verticalLines.length >= 2) {
    // Sort lines
    horizontalLines.sort((a, b) => a.y - b.y);
    verticalLines.sort((a, b) => a.x - b.x);
    
    // Look for rectangular patterns
    for (let i = 0; i < horizontalLines.length - 1; i++) {
      for (let j = i + 1; j < horizontalLines.length; j++) {
        const top = horizontalLines[i];
        const bottom = horizontalLines[j];
        const rectHeight = bottom.y - top.y;
        
        if (rectHeight < height * 0.2 || rectHeight > height * 0.7) continue;
        
        for (let k = 0; k < verticalLines.length - 1; k++) {
          for (let l = k + 1; l < verticalLines.length; l++) {
            const left = verticalLines[k];
            const right = verticalLines[l];
            const rectWidth = right.x - left.x;
            
            if (rectWidth < width * 0.3 || rectWidth > width * 0.85) continue;
            
            // Check if this forms a business card-like rectangle
            const aspectRatio = rectWidth / rectHeight;
            if (aspectRatio > 1.3 && aspectRatio < 1.9) { // Business card aspect ratio
              rectangles.push({
                x: left.x,
                y: top.y,
                width: rectWidth,
                height: rectHeight,
                aspectRatio
              });
            }
          }
        }
      }
    }
  }
  
  return rectangles;
}

/**
 * Find the best card candidate from detected rectangles
 */
function findBestCardCandidate(rectangles, frameWidth, frameHeight) {
  if (rectangles.length === 0) return null;
  
  // Score each rectangle based on size, position, and aspect ratio
  let bestRect = null;
  let bestScore = 0;
  
  const idealAspectRatio = 1.586; // Standard business card
  const centerX = frameWidth / 2;
  const centerY = frameHeight / 2;
  
  for (const rect of rectangles) {
    // Size score (prefer larger cards that fill the frame well)
    const sizeRatio = (rect.width * rect.height) / (frameWidth * frameHeight);
    const sizeScore = sizeRatio > 0.15 && sizeRatio < 0.6 ? 1 : 0;
    
    if (sizeScore === 0) continue;
    
    // Aspect ratio score
    const aspectDiff = Math.abs(rect.aspectRatio - idealAspectRatio);
    const aspectScore = aspectDiff < 0.3 ? 1 : 0;
    
    if (aspectScore === 0) continue;
    
    // Position score (prefer centered cards)
    const rectCenterX = rect.x + rect.width / 2;
    const rectCenterY = rect.y + rect.height / 2;
    const centerDist = Math.sqrt(
      Math.pow((rectCenterX - centerX) / frameWidth, 2) +
      Math.pow((rectCenterY - centerY) / frameHeight, 2)
    );
    const positionScore = Math.max(0, 1 - centerDist * 2);
    
    const totalScore = sizeScore + aspectScore + positionScore;
    
    if (totalScore > bestScore) {
      bestScore = totalScore;
      bestRect = rect;
    }
  }
  
  return bestScore > 1.5 ? bestRect : null;
}

/**
 * Verify the card is well-positioned within the expected guide area
 */
function verifyCardPosition(cardRect, frameWidth, frameHeight) {
  // Expected guide frame area (80% width, centered, business card aspect ratio)
  const guideWidth = frameWidth * 0.8;
  const guideHeight = guideWidth / 1.586;
  const guideX = (frameWidth - guideWidth) / 2;
  const guideY = (frameHeight - guideHeight) / 2;
  
  // Check if card is reasonably within guide area (allow some tolerance)
  const tolerance = 0.15;
  
  const cardCenterX = cardRect.x + cardRect.width / 2;
  const cardCenterY = cardRect.y + cardRect.height / 2;
  const guideCenterX = guideX + guideWidth / 2;
  const guideCenterY = guideY + guideHeight / 2;
  
  const centerDistX = Math.abs(cardCenterX - guideCenterX) / frameWidth;
  const centerDistY = Math.abs(cardCenterY - guideCenterY) / frameHeight;
  
  // Card must be reasonably centered
  if (centerDistX > tolerance || centerDistY > tolerance) {
    return false;
  }
  
  // Card size should be similar to guide size (within range)
  const sizeRatioW = cardRect.width / guideWidth;
  const sizeRatioH = cardRect.height / guideHeight;
  
  if (sizeRatioW < 0.7 || sizeRatioW > 1.3 || sizeRatioH < 0.7 || sizeRatioH > 1.3) {
    return false;
  }
  
  return true;
}

/**
 * Calculate contrast around the card edges
 */
function calculateContrast(imageData, cardRect) {
  const { data, width } = imageData;
  let contrastSum = 0;
  let samples = 0;
  
  // Sample points around the rectangle perimeter
  const samplePoints = 20;
  
  for (let i = 0; i < samplePoints; i++) {
    // Top edge
    const x1 = Math.floor(cardRect.x + (cardRect.width * i) / samplePoints);
    const y1 = Math.floor(cardRect.y);
    
    // Bottom edge
    const x2 = Math.floor(cardRect.x + (cardRect.width * i) / samplePoints);
    const y2 = Math.floor(cardRect.y + cardRect.height);
    
    if (y1 > 0 && y1 < imageData.height - 1) {
      const contrast1 = getPixelContrast(data, x1, y1, width);
      if (!isNaN(contrast1)) {
        contrastSum += contrast1;
        samples++;
      }
    }
    
    if (y2 > 0 && y2 < imageData.height - 1) {
      const contrast2 = getPixelContrast(data, x2, y2, width);
      if (!isNaN(contrast2)) {
        contrastSum += contrast2;
        samples++;
      }
    }
  }
  
  for (let i = 0; i < samplePoints; i++) {
    // Left edge
    const x3 = Math.floor(cardRect.x);
    const y3 = Math.floor(cardRect.y + (cardRect.height * i) / samplePoints);
    
    // Right edge
    const x4 = Math.floor(cardRect.x + cardRect.width);
    const y4 = Math.floor(cardRect.y + (cardRect.height * i) / samplePoints);
    
    if (x3 > 0 && x3 < width - 1) {
      const contrast3 = getPixelContrast(data, x3, y3, width);
      if (!isNaN(contrast3)) {
        contrastSum += contrast3;
        samples++;
      }
    }
    
    if (x4 > 0 && x4 < width - 1) {
      const contrast4 = getPixelContrast(data, x4, y4, width);
      if (!isNaN(contrast4)) {
        contrastSum += contrast4;
        samples++;
      }
    }
  }
  
  return samples > 0 ? contrastSum / samples : 0;
}

function getPixelContrast(data, x, y, width) {
  const idx = (y * width + x) * 4;
  const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
  
  // Compare with neighbors
  const neighbors = [
    (data[((y - 1) * width + x) * 4] + data[((y - 1) * width + x) * 4 + 1] + data[((y - 1) * width + x) * 4 + 2]) / 3,
    (data[((y + 1) * width + x) * 4] + data[((y + 1) * width + x) * 4 + 1] + data[((y + 1) * width + x) * 4 + 2]) / 3,
    (data[(y * width + (x - 1)) * 4] + data[(y * width + (x - 1)) * 4 + 1] + data[(y * width + (x - 1)) * 4 + 2]) / 3,
    (data[(y * width + (x + 1)) * 4] + data[(y * width + (x + 1)) * 4 + 1] + data[(y * width + (x + 1)) * 4 + 2]) / 3,
  ];
  
  let maxDiff = 0;
  for (const neighbor of neighbors) {
    maxDiff = Math.max(maxDiff, Math.abs(gray - neighbor));
  }
  
  return maxDiff;
}

/**
 * Calculate average brightness of the image
 */
function calculateBrightness(imageData) {
  const { data, width, height } = imageData;
  let totalBrightness = 0;
  let count = 0;
  
  // Sample every 5th pixel for performance
  for (let y = 0; y < height; y += 5) {
    for (let x = 0; x < width; x += 5) {
      const idx = (y * width + x) * 4;
      const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
      totalBrightness += brightness;
      count++;
    }
  }
  
  return totalBrightness / count;
}

/**
 * Get user-friendly message based on detection result
 */
export const getDetectionMessage = (result) => {
  if (result.detected) {
    return { text: '✓ Card detected - Hold steady...', color: '#4CAF50', icon: '✓' };
  }
  
  switch (result.reason) {
    case 'blurry':
      return { text: 'Hold steady - Image is blurry', color: '#FF9800', icon: '⚠' };
    case 'no_card_detected':
      return { text: 'Position card in frame', color: '#2196F3', icon: 'ℹ' };
    case 'card_not_aligned':
      return { text: 'Center card within guide', color: '#FF9800', icon: '⚠' };
    case 'low_contrast':
      return { text: 'Improve lighting or angle', color: '#FF9800', icon: '💡' };
    case 'too_dark':
      return { text: 'Too dark - Need more light', color: '#FF9800', icon: '💡' };
    case 'too_bright':
      return { text: 'Too bright - Reduce glare', color: '#FF9800', icon: '☀' };
    case 'video_not_ready':
      return { text: 'Camera initializing...', color: '#9E9E9E', icon: '⏳' };
    default:
      return { text: 'Position card in guide frame', color: '#2196F3', icon: 'ℹ' };
  }
};

